import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase, describeError } from "@/lib/supabase";

/**
 * Password reset landing page.
 *
 * Users arrive here from the recovery link Supabase emails them (URL contains
 * `#type=recovery&access_token=...`). Supabase's onAuthStateChange fires a
 * `PASSWORD_RECOVERY` event and puts the app into a temporary session that can
 * only call auth.updateUser({ password }). After success we sign them out and
 * push them to /auth to sign in fresh.
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);

  useEffect(() => {
    // Supabase parses the hash on load and fires PASSWORD_RECOVERY.
    // If the hash isn't present, the user shouldn't be on this page.
    const hash = window.location.hash || "";
    if (hash.includes("type=recovery") || hash.includes("access_token")) {
      setRecoveryReady(true);
    } else {
      // Also allow direct nav if a session already exists (e.g. reload after
      // Supabase consumed the hash)
      supabase.auth.getSession().then(({ data }) => {
        if (data?.session) setRecoveryReady(true);
        else navigate("/auth", { replace: true });
      });
    }
  }, [navigate]);

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw new Error(describeError(err));
      // Sign out the recovery session so user has to log in fresh
      await supabase.auth.signOut();
      setDone(true);
    } catch (err) {
      setError(err?.message || "Couldn't update your password. Please request a new reset link.");
    } finally {
      setLoading(false);
    }
  };

  if (!recoveryReady) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6" data-testid="reset-password-page">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Logo size="md" />

        {done ? (
          <div className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="reset-password-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
              <div>
                <h1 className="font-heading text-xl font-extrabold text-foreground">
                  Password updated
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Your new password is now active. Sign in to continue.
                </p>
                <Button
                  className="mt-5"
                  onClick={() => navigate("/auth")}
                  data-testid="reset-password-go-signin"
                >
                  Sign in
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h1 className="mt-8 font-heading text-2xl font-extrabold tracking-tight">
              Set a new password
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose something strong — at least 6 characters, ideally a mix of letters and numbers
              you don't reuse elsewhere.
            </p>

            <form onSubmit={handleReset} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  className="mt-1.5"
                  data-testid="reset-password-new"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  required
                  minLength={6}
                  className="mt-1.5"
                  data-testid="reset-password-confirm"
                />
              </div>

              {error && (
                <div
                  className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  data-testid="reset-password-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={loading}
                data-testid="reset-password-submit"
              >
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
